import {
  signPsbtWithProxyWallet,
  derivePublicKeyFromPrivateKey,
  deriveAddressFromPrivateKey,
} from './bitcoinUtils';
import * as bitcoin from 'bitcoinjs-lib';
import {
  getMempoolTxUrl,
  getMempoolTxHexUrl,
  getMempoolBroadcastUrl,
  getMempoolTxStatusUrl,
  getMempoolAddressUrl,
} from './mempoolProvider';

/**
 * Get token ID from ordinal - tries multiple possible field names
 * @param {Object} ordinal - The ordinal object
 * @returns {string|null} Token ID or null
 */
export const getTokenId = (ordinal) => {
  if (!ordinal) return null;
  return (
    ordinal.tokenId ||
    // Satflow item / wallet-contents shape
    ordinal.inscriptionId ||
    ordinal.id ||
    (ordinal.inscriptionNumber
      ? `${ordinal.txid || ordinal.genesisTransaction || ''}i${ordinal.inscriptionNumber}`
      : null)
  );
};

/**
 * Get inscription ID for Magic Eden link
 * @param {Object} ordinal - The ordinal object
 * @returns {string|null} Inscription ID
 */
export const getInscriptionId = (ordinal) => {
  if (!ordinal) return null;
  return (
    ordinal.inscriptionId ||
    ordinal.inscriptionId ||
    (ordinal.inscriptionNumber
      ? `${ordinal.txid || ordinal.genesisTransaction || ''}i${ordinal.inscriptionNumber}`
      : null)
  );
};

/**
 * Fetch specific token by ID to check ownership (no cache)
 * This is more reliable than wallet-tokens for checking purchase status
 * @param {string} tokenId - Token inscription ID
 * @returns {Promise<Object|null>} Token object or null
 */
export const fetchTokenById = async (tokenId) => {
  try {
    // Log the request for debugging
    console.log(`[fetchTokenById] Fetching token: ${tokenId}`);

    // Use Satflow item endpoint (proxied) to look up inscription details
    const params = new URLSearchParams({
      inscriptionId: tokenId,
      listing: 'true',
      bid: 'false',
      metadata: 'false',
    });

    const response = await fetch(`/api/satflow-item?${params.toString()}`);

    if (!response.ok) {
      console.error(`[fetchTokenById] HTTP error: ${response.status}`);
      return null;
    }

    const data = await response.json();
    const item = data?.data || data;

    if (item && item.owner) {
      console.log(`[fetchTokenById] Found token, owner: ${item.owner}`);
      return item;
    }

    console.log(`[fetchTokenById] No token found for ID: ${tokenId}`);
    return null;
  } catch (err) {
    console.error('[fetchTokenById] Error:', err);
    return null;
  }
};

/**
 * Normalize one row from GET /v1/activity/listings into the ordinal shape used by
 * prepareSecurePurchase / listing helpers (listed + listedPrice + tokenId).
 */
function normalizeSatflowActivityListing(row, fallbackCollectionSlug) {
  if (!row || typeof row !== 'object') return null;

  const ask = row.ask && typeof row.ask === 'object' ? row.ask : null;
  // Do not use row.id — activity rows use it for listing event id, not inscription
  const inscriptionId = (ask && ask.inscriptionId) || row.inscriptionId || null;
  if (!inscriptionId || typeof inscriptionId !== 'string') return null;

  const unit = row.unitPrice ?? row.price;
  const askPrice = ask && ask.price;
  const listedPrice =
    typeof unit === 'number' && unit > 0
      ? unit
      : typeof askPrice === 'number' && askPrice > 0
        ? askPrice
        : null;
  if (!listedPrice) return null;

  if (row.cancelledAt != null || row.invalidAt != null) return null;
  if (row.fillPendingAt != null) return null;
  if (row.orderType && String(row.orderType).toLowerCase() !== 'ask') {
    return null;
  }

  const owner =
    (ask && (ask.sellerOrdAddress || ask.sellerReceiveAddress)) ||
    row.owner ||
    null;

  const mempoolTxId = ask && ask.unconfirmed === true ? 'unconfirmed' : '';

  return {
    inscriptionId,
    id: inscriptionId,
    tokenId: inscriptionId,
    inscriptionNumber: ask?.inscriptionNumber ?? row.inscriptionNumber,
    contentType: ask?.inscriptionContentType || null,
    listed: true,
    listedPrice,
    collectionSymbol:
      (ask && ask.collectionSlug) ||
      row.collectionSlug ||
      fallbackCollectionSlug,
    owner,
    mempoolTxId,
    _satflowRaw: row,
  };
}

/**
 * Satflow GET /v1/activity/listings — paginated, sorted listings for a collection slug.
 * @returns {Promise<{ items: Array, total: number }>}
 */
export const fetchSatflowActivityListings = async (
  collectionSymbol,
  bypassCache = false,
  options = {}
) => {
  const empty = { items: [], total: 0 };
  try {
    if (!collectionSymbol) {
      return empty;
    }

    const pageSize = Math.min(
      250,
      Math.max(1, Number(options.pageSize) || 100)
    );
    const page = Math.max(1, parseInt(String(options.page || '1'), 10) || 1);
    const sortDirection =
      String(options.sortDirection || 'asc').toLowerCase() === 'desc'
        ? 'desc'
        : 'asc';
    const sortBy =
      options.sortBy != null ? String(options.sortBy) : 'unitPrice';

    const queryParams = new URLSearchParams({
      collectionSlug: String(collectionSymbol).trim(),
      page: String(page),
      pageSize: String(pageSize),
      timeRange: options.timeRange != null ? String(options.timeRange) : '30d',
      includeOnlyCollectionItems: 'true',
      sortBy,
      sortDirection,
    });

    if (bypassCache) {
      queryParams.append('_t', Date.now().toString());
    }

    const response = await fetch(
      `/api/satflow-activity-listings?${queryParams.toString()}`
    );
    if (!response.ok) {
      return empty;
    }

    const data = await response.json();
    const payload = data?.data || data;
    const rawListings = Array.isArray(payload?.listings)
      ? payload.listings
      : Array.isArray(payload?.items)
        ? payload.items
        : [];

    const normalized = rawListings
      .map((row) => normalizeSatflowActivityListing(row, collectionSymbol))
      .filter(Boolean);

    if (sortDirection === 'desc') {
      normalized.sort((a, b) => (b.listedPrice || 0) - (a.listedPrice || 0));
    } else {
      normalized.sort((a, b) => (a.listedPrice || 0) - (b.listedPrice || 0));
    }

    const total =
      typeof payload?.total === 'number' && payload.total >= 0
        ? payload.total
        : normalized.length;

    return { items: normalized, total };
  } catch (err) {
    console.error('Error fetching Satflow activity listings:', err);
    return empty;
  }
};

/**
 * Optional: merge display fields from GET /v1/item for grid thumbnails (Satflow).
 */
export const enrichSatflowListingItemForDisplay = async (item) => {
  const inscriptionId = item?.inscriptionId || item?.tokenId;
  if (!inscriptionId) return item;

  try {
    const params = new URLSearchParams({
      inscriptionId: String(inscriptionId),
      listing: 'true',
      bid: 'false',
      metadata: 'true',
    });
    const response = await fetch(`/api/satflow-item?${params.toString()}`);
    if (!response.ok) return { ...item, _satflowListing: true };

    const data = await response.json();
    const d = data?.data || data;
    if (!d || typeof d !== 'object') return { ...item, _satflowListing: true };

    const imageUrl =
      d.image_url ||
      d.imageUrl ||
      d.contentURI ||
      d.content_uri ||
      d.preview_url ||
      d.previewUrl ||
      null;
    const contentType =
      d.content_type ||
      d.contentType ||
      item._satflowRaw?.ask?.inscriptionContentType ||
      null;
    const name =
      d.name ||
      d.title ||
      (d.metadata && typeof d.metadata === 'object' && d.metadata.name) ||
      null;
    const inscriptionNumber =
      d.inscription_number != null
        ? d.inscription_number
        : d.inscriptionNumber != null
          ? d.inscriptionNumber
          : item.inscriptionNumber;

    return {
      ...item,
      inscriptionNumber,
      contentURI: imageUrl || item.contentURI,
      contentPreviewURI: imageUrl || item.contentPreviewURI,
      contentType: contentType || item.contentType,
      meta: name ? { ...(item.meta || {}), name } : item.meta,
      _satflowListing: true,
    };
  } catch {
    return { ...item, _satflowListing: true };
  }
};

/**
 * Fetch collection items sorted by price (lowest first)
 * ONLY use when listing items, not for checking confirmations
 * @param {string} collectionSymbol - Collection symbol (Satflow collection slug)
 * @param {boolean} bypassCache - Whether to bypass cache (for auto-trading)
 * @param {Object} [options]
 * @param {number} [options.pageSize] - Satflow page size (default 100)
 * @param {number} [options.page] - Page number (default 1)
 * @param {string} [options.sortDirection] - asc | desc
 * @returns {Promise<Array>} Array of items
 */
export const fetchCollectionItems = async (
  collectionSymbol,
  bypassCache = false,
  options = {}
) => {
  const { items } = await fetchSatflowActivityListings(
    collectionSymbol,
    bypassCache,
    options
  );
  return items;
};

/**
 * Satflow GET /v1/activity/bids — collection bid activity (buy offers).
 * @param {string} collectionSlug - Satflow collection slug (e.g. fine_pepes)
 * @param {Object} [options]
 * @returns {Promise<{ bids: Array, total: number }>}
 */
export const fetchSatflowCollectionBids = async (
  collectionSlug,
  options = {}
) => {
  const empty = { bids: [], total: 0 };
  try {
    const slug = collectionSlug ? String(collectionSlug).trim() : '';
    if (!slug) return empty;

    const pageSize = Math.min(
      250,
      Math.max(1, Number(options.pageSize) || 100)
    );
    const page = Math.max(1, parseInt(String(options.page || '1'), 10) || 1);
    const sortDirection =
      String(options.sortDirection || 'desc').toLowerCase() === 'asc'
        ? 'asc'
        : 'desc';
    const sortBy =
      options.sortBy != null ? String(options.sortBy) : 'unitPrice';
    const timeRange =
      options.timeRange != null ? String(options.timeRange) : '30d';

    const queryParams = new URLSearchParams({
      collectionSlug: slug,
      timeRange,
      sortBy,
      sortDirection,
      page: String(page),
      pageSize: String(pageSize),
    });

    if (options.bypassCache) {
      queryParams.append('_t', Date.now().toString());
    }

    const response = await fetch(
      `/api/satflow-activity-bids?${queryParams.toString()}`
    );
    if (!response.ok) return empty;

    const data = await response.json();
    const payload = data?.data || data;
    const bids = Array.isArray(payload?.bids) ? payload.bids : [];
    const total =
      typeof payload?.total === 'number' && payload.total >= 0
        ? payload.total
        : bids.length;

    return { bids, total };
  } catch (err) {
    console.error('Error fetching Satflow collection bids:', err);
    return empty;
  }
};

/**
 * Get floor price from collection (only use when listing)
 * Uses Satflow collection-stats/floors API: GET .../floors?type=ordinals&slugs=<slug>
 * @param {string} collectionSymbol - Collection symbol (slug), e.g. fine_pepes
 * @param {boolean} bypassCache - Bypass cache (adds cache-bust query param)
 * @returns {Promise<number|null>} Floor price in sats or null
 */
export const getFloorPrice = async (collectionSymbol, bypassCache = false) => {
  try {
    const slug = collectionSymbol ? String(collectionSymbol).trim() : '';
    if (!slug) {
      return null;
    }

    const queryParams = new URLSearchParams({
      type: 'ordinals',
      slugs: slug,
    });
    if (bypassCache) {
      queryParams.append('_t', Date.now().toString());
    }

    const response = await fetch(
      `/api/satflow-collection-floors?${queryParams.toString()}`
    );

    const raw = await response.text();
    let data;
    try {
      data = JSON.parse(raw);
    } catch {
      data = null;
    }

    if (response.ok && data) {
      // Response: { success: true, data: [ { slug, floor, timestamp, listedCount, ... } ] }
      const list = Array.isArray(data.data)
        ? data.data
        : Array.isArray(data)
          ? data
          : [];
      const slugLower = slug.toLowerCase();
      const entry =
        list.find((e) => {
          const s = (e.slug || e.collectionId || '').toLowerCase();
          return s && (s === slugLower || s === slug);
        }) || list[0];

      if (entry) {
        const floorVal = entry.floor;
        const floor =
          typeof floorVal === 'number' && floorVal > 0
            ? floorVal
            : typeof floorVal === 'string'
              ? parseInt(floorVal, 10)
              : Number(floorVal);
        if (!Number.isNaN(floor) && floor > 0) {
          return floor;
        }
      }
    }

    // Fallback: derive floor from collection items if floors API is unavailable
    const items = await fetchCollectionItems(slug, bypassCache);
    const listedItems = items
      .filter((item) => item.listed && item.listedPrice)
      .sort((a, b) => (a.listedPrice || 0) - (b.listedPrice || 0));

    if (listedItems.length > 0) {
      return listedItems[0].listedPrice;
    }

    return null;
  } catch (err) {
    console.error('Error getting floor price:', err);
    return null;
  }
};

/**
 * Fetch all tokens owned by a wallet for a specific collection
 * @param {string} ownerAddress - Wallet address
 * @param {string} collectionSymbol - Collection symbol to filter
 * @param {boolean} bypassCache - Whether to bypass cache
 * @returns {Promise<Array>} Array of ordinals
 */
export const fetchWalletOrdinals = async (
  ownerAddress,
  collectionSymbol = null,
  bypassCache = false
) => {
  try {
    if (!ownerAddress) {
      return [];
    }

    const queryParams = new URLSearchParams({
      address: ownerAddress,
      itemType: 'inscription',
      limit: '100',
    });

    if (collectionSymbol) {
      // Satflow uses collection slug under "collection" query param
      queryParams.append('collection', collectionSymbol);
    }

    if (bypassCache) {
      queryParams.append('_t', Date.now().toString());
    }

    const response = await fetch(
      `/api/satflow-wallet-contents?${queryParams.toString()}`
    );
    if (response.ok) {
      const data = await response.json();
      const payload = data?.data || data;
      const ordinals = payload?.results?.ordinals || [];

      // Normalize Satflow wallet contents into the shape expected by trading logic
      return ordinals.map((entry) => {
        const token = entry.token || {};
        const collection = entry.collection || {};
        const listing = entry.listing || null;

        const inscriptionId =
          token.inscription_id ||
          token.id ||
          (token.inscriptionNumber
            ? `${token.txid || token.genesisTransaction || ''}i${token.inscriptionNumber}`
            : null);

        const listedPrice =
          listing && typeof listing.price === 'number' ? listing.price : null;

        return {
          // Identification
          inscriptionId,
          id: inscriptionId,
          tokenId: inscriptionId,
          inscriptionNumber:
            token.inscription_number ?? token.inscriptionNumber,
          // Media / content
          contentURI: token.image_url || token.contentURI || null,
          contentPreviewURI: token.image_url || token.contentPreviewURI || null,
          contentType: token.content_type || token.contentType || null,
          // Ownership and collection
          owner: entry.owner || ownerAddress,
          collectionSymbol:
            collection.slug || collection.id || collection.collectionSlug,
          // Listing state
          listed: !!listedPrice,
          listedPrice,
          // Satflow raw data for debugging/extensibility
          _satflowRaw: entry,
        };
      });
    }
    return [];
  } catch (err) {
    console.error('Error fetching wallet ordinals:', err);
    return [];
  }
};

/**
 * Check if a purchase is confirmed by checking token ownership
 * This is more reliable than checking mempool status
 * @param {string} tokenId - Token inscription ID
 * @param {string} expectedOwner - Expected owner address
 * @returns {Promise<boolean>} True if token is owned by expectedOwner
 */
export const checkPurchaseConfirmed = async (tokenId, expectedOwner) => {
  try {
    console.log(`[checkPurchaseConfirmed] Checking tokenId: ${tokenId}`);
    console.log(`[checkPurchaseConfirmed] Expected owner: ${expectedOwner}`);

    const token = await fetchTokenById(tokenId);
    if (!token) {
      console.log(`[checkPurchaseConfirmed] Token not found`);
      return false;
    }

    console.log(`[checkPurchaseConfirmed] Token owner: ${token.owner}`);
    console.log(
      `[checkPurchaseConfirmed] Match: ${token.owner === expectedOwner}`
    );

    // Check if owner matches expected owner
    return token.owner === expectedOwner;
  } catch (err) {
    console.error('[checkPurchaseConfirmed] Error:', err);
    return false;
  }
};

/**
 * Check if an item has a pending transaction (mempool tx)
 * Items without mempoolTxId are ready to be listed/traded
 * @param {Object} item - The item/token object
 * @returns {boolean} True if item has a pending transaction
 */
export const hasPendingTransaction = (item) => {
  return !!(item.mempoolTxId && item.mempoolTxId !== '');
};

/**
 * List an ordinal for sale using proxy wallet (Satflow: intent/sell → sign → /list).
 * @param {Object} ordinal - The ordinal to list
 * @param {number} priceInSats - Price in satoshis
 * @param {Object} wallet - Proxy wallet object
 * @param {string} network - Network type
 * @returns {Promise<Object>} Result with success, txid, error
 */
export const listOrdinalWithProxyWallet = async (
  ordinal,
  priceInSats,
  wallet,
  network = 'mainnet'
) => {
  try {
    const tokenId = getTokenId(ordinal);
    if (!tokenId) {
      throw new Error('Could not determine token ID from ordinal');
    }

    let address, publicKey;
    try {
      address = deriveAddressFromPrivateKey(wallet.privateKey, network);
      publicKey = derivePublicKeyFromPrivateKey(wallet.privateKey, network);
    } catch (err) {
      console.error('Error deriving address from private key:', err);
      address = wallet.address;
      publicKey = wallet.publicKey;
    }

    if (!address || !publicKey) {
      throw new Error('Could not get wallet address or public key');
    }

    const priceInSatsInt = Math.round(parseFloat(priceInSats) || 0);

    // Satflow create listing intent: POST /intent/sell (proxied as /api/satflow-intent-sell)
    const intentPayload = {
      price: priceInSatsInt,
      inscriptionId: tokenId,
      sellerOrdAddress: address,
      sellerReceiveAddress: address,
      tapInternalKey:
        publicKey && publicKey.length >= 64 ? publicKey : undefined,
    };

    const fetchResponse = await fetch('/api/satflow-intent-sell', {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
      },
      body: JSON.stringify(intentPayload),
    });

    if (!fetchResponse.ok) {
      const errorText = await fetchResponse.text();
      let errorMessage = `Satflow intent/sell error: ${fetchResponse.status}`;
      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.error && typeof errorJson.error === 'string') {
          errorMessage = errorJson.error;
        } else if (errorJson.message) {
          errorMessage = errorJson.message;
        }
      } catch (e) {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const fetchData = await fetchResponse.json();
    const seller = fetchData?.data?.seller;
    if (!seller) {
      throw new Error('Satflow intent/sell did not return data.seller');
    }

    // Response shape: data.seller.unsignedListingPSBTBase64, data.seller.secureListingPSBTs[]
    const unsignedListingBase64 =
      seller.unsignedListingPSBTBase64 || seller.unsignedListingPSBTHex;
    if (!unsignedListingBase64) {
      throw new Error('Satflow intent/sell missing unsignedListingPSBTBase64');
    }

    // If API returns hex, convert to base64 for signing (signPsbtWithProxyWallet expects base64)
    let unsignedListingPsbtB64 = unsignedListingBase64;
    if (
      unsignedListingBase64.length > 0 &&
      !unsignedListingBase64.includes(' ')
    ) {
      const isHex = /^[0-9a-fA-F]+$/.test(unsignedListingBase64);
      if (isHex) {
        const bytes = new Uint8Array(
          unsignedListingBase64.match(/.{1,2}/g).map((b) => parseInt(b, 16))
        );
        unsignedListingPsbtB64 = btoa(
          Array.from(bytes)
            .map((byte) => String.fromCharCode(byte))
            .join('')
        );
      }
    }

    // Step 2: Sign main listing PSBT (insecure/snipable listing)
    const signedListing = await signPsbtWithProxyWallet(
      unsignedListingPsbtB64.trim(),
      wallet.privateKey,
      network,
      {
        finalize: false,
        extractTx: false,
        expectedPublicKey: publicKey,
        walletAddress: address,
      }
    );

    if (!signedListing || !signedListing.base64) {
      throw new Error('Failed to sign listing PSBT');
    }

    // Sign each secure listing PSBT (non-snipable)
    const secureListingPsbtList = seller.secureListingPSBTs || [];
    const signedSecureListingPSBTs = [];
    for (let i = 0; i < secureListingPsbtList.length; i++) {
      const secureItem = secureListingPsbtList[i];
      const secureB64 = secureItem.base64 || secureItem.hex;
      if (!secureB64) continue;
      let secureB64ForSign = secureB64;
      if (/^[0-9a-fA-F]+$/.test(secureB64)) {
        const bytes = new Uint8Array(
          secureB64.match(/.{1,2}/g).map((b) => parseInt(b, 16))
        );
        secureB64ForSign = btoa(
          Array.from(bytes)
            .map((byte) => String.fromCharCode(byte))
            .join('')
        );
      }
      const signedSecure = await signPsbtWithProxyWallet(
        secureB64ForSign.trim(),
        wallet.privateKey,
        network,
        {
          finalize: false,
          extractTx: false,
          expectedPublicKey: publicKey,
          walletAddress: address,
        }
      );
      if (signedSecure && signedSecure.base64) {
        signedSecureListingPSBTs.push(signedSecure.base64);
      }
    }

    // Step 3: Submit to Satflow POST /list (proxied as /api/satflow-list)
    const listingEntry = {
      price: priceInSatsInt,
      inscriptionId: tokenId,
      sellerOrdAddress: address,
      sellerReceiveAddress: address,
      tapInternalKey:
        publicKey && publicKey.length >= 64 ? publicKey : undefined,
    };

    const listPayload = {
      listings: [listingEntry],
      signedListingPSBT: signedListing.base64,
      signedSecureListingPSBTs:
        signedSecureListingPSBTs.length > 0 ? signedSecureListingPSBTs : [],
      unsignedListingPSBT: unsignedListingPsbtB64,
    };

    const listResponse = await fetch('/api/satflow-list', {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
      },
      body: JSON.stringify(listPayload),
    });

    if (!listResponse.ok) {
      const errorText = await listResponse.text();
      let errorMessage = `Satflow /list error: ${listResponse.status}`;
      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.error && typeof errorJson.error === 'string') {
          errorMessage = errorJson.error;
        } else if (errorJson.message) {
          errorMessage = errorJson.message;
        }
      } catch (e) {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const listData = await listResponse.json();
    const success = listData?.success === true;
    const txid = listData?.data?.txid;

    return {
      success,
      listed: success ? 1 : 0,
      txid,
      failed: success ? [] : [tokenId],
      data: listData,
    };
  } catch (err) {
    console.error('Error listing ordinal:', err);
    return {
      success: false,
      error: err.message || 'Failed to list ordinal',
    };
  }
};

/** Error message returned by Satflow when buyer needs to create dummy UTXOs first */
const SATFLOW_DUMMY_UTXO_ERROR = 'Additional dummy UTXOs required to purchase';

/** localStorage key for pending secure-purchase preps (survives refresh/restart so we don't create duplicate preps) */
const PENDING_PREPS_STORAGE_KEY = 'fine-trading-pending-secure-preps';

/** Max time to wait for prep tx confirmation (60 min); Bitcoin can take longer in congestion. */
export const PREP_CONFIRM_TIMEOUT_MS = 60 * 60 * 1000;

function getPendingPrepsFromStorage() {
  try {
    const raw =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem(PENDING_PREPS_STORAGE_KEY)
        : null;
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function savePendingPrepsToStorage(preps) {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(PENDING_PREPS_STORAGE_KEY, JSON.stringify(preps));
    }
  } catch (e) {
    console.warn('Could not save pending preps to localStorage', e);
  }
}

/** Get pending prep for (inscriptionId, buyerAddress) if any */
export function getPendingPrep(inscriptionId, buyerAddress) {
  const key = `${(inscriptionId || '').toLowerCase()}|${(buyerAddress || '').toLowerCase()}`;
  return (
    getPendingPrepsFromStorage().find(
      (p) =>
        `${(p.inscriptionId || '').toLowerCase()}|${(p.buyerAddress || '').toLowerCase()}` ===
        key
    ) || null
  );
}

/** Add or replace pending prep (so we don't create another prep after restart) */
export function addPendingPrep(entry) {
  const preps = getPendingPrepsFromStorage().filter(
    (p) =>
      !(
        p.inscriptionId === entry.inscriptionId &&
        p.buyerAddress === entry.buyerAddress
      )
  );
  preps.push({
    inscriptionId: entry.inscriptionId,
    buyerAddress: entry.buyerAddress,
    prepTxid: entry.prepTxid,
    signedPaymentPrepPSBT: entry.signedPaymentPrepPSBT,
    intentPayload: entry.intentPayload,
    timestamp: entry.timestamp ?? Date.now(),
  });
  savePendingPrepsToStorage(preps);
}

/** Remove pending prep after successful purchase or on purpose */
export function removePendingPrep(inscriptionId, buyerAddress) {
  const preps = getPendingPrepsFromStorage().filter(
    (p) =>
      !(p.inscriptionId === inscriptionId && p.buyerAddress === buyerAddress)
  );
  savePendingPrepsToStorage(preps);
}

/** Wait until all given prep txids are confirmed or timeout. Logs periodically. */
export async function waitForPrepsToConfirm(prepTxids, options = {}) {
  const {
    timeoutMs = PREP_CONFIRM_TIMEOUT_MS,
    addConsoleLog = null,
    isStopRequested = null,
    pollIntervalMs = 5000,
  } = options;
  if (!prepTxids || prepTxids.length === 0) return;
  const set = new Set(prepTxids.filter(Boolean));
  const start = Date.now();
  let lastLog = 0;
  while (set.size > 0 && Date.now() - start < timeoutMs) {
    if (typeof isStopRequested === 'function' && isStopRequested()) return;
    for (const txid of set) {
      const confirmed = await checkTransactionConfirmed(
        txid,
        options.network || 'mainnet'
      );
      if (confirmed) set.delete(txid);
    }
    if (
      set.size > 0 &&
      typeof addConsoleLog === 'function' &&
      Date.now() - lastLog >= 30000
    ) {
      const waited = Math.round((Date.now() - start) / 1000);
      addConsoleLog(
        `  Waiting for ${set.size} prep tx(s) to confirm... (${waited}s elapsed, timeout ${timeoutMs / 1000}s)`
      );
      lastLog = Date.now();
    }
    if (set.size > 0) await new Promise((r) => setTimeout(r, pollIntervalMs));
  }
  if (set.size > 0 && typeof addConsoleLog === 'function') {
    addConsoleLog(
      `  ⚠ ${set.size} prep tx(s) did not confirm within ${timeoutMs / 1000}s. You can wait longer and complete purchases later.`
    );
  }
}

/**
 * Create a dummy UTXO via Satflow backend (setupUtxos), sign, broadcast, and wait for confirmation.
 * Call this when purchase intent returns "Additional dummy UTXOs required to purchase".
 * @param {string} ownerAddress - Buyer wallet address (ordinals address)
 * @param {Object} wallet - Proxy wallet object (privateKey, address)
 * @param {string} network - Network type
 * @param {Function} [addConsoleLog] - Optional logger
 * @returns {Promise<{ success: boolean, txid?: string, error?: string }>}
 */
export const createDummyUtxoAndWait = async (
  ownerAddress,
  wallet,
  network = 'mainnet',
  addConsoleLog = null
) => {
  const log = (msg) => {
    if (typeof addConsoleLog === 'function') addConsoleLog(msg);
    else console.log('[createDummyUtxoAndWait]', msg);
  };

  try {
    const res = await fetch('/api/satflow-setup-utxos', {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-wallet-address': ownerAddress,
      },
      body: JSON.stringify({ 0: { json: {} } }),
    });

    if (!res.ok) {
      const errText = await res.text();
      throw new Error(`Setup UTXOs request failed: ${res.status} ${errText}`);
    }

    const data = await res.json();
    const json = data?.result?.data?.json ?? data?.data?.json ?? data?.json;
    const unsignedB64 = json?.unsignedPSBTBase64 ?? json?.unsignedPSBTHex;
    if (!unsignedB64) {
      throw new Error('Setup UTXOs response missing unsigned PSBT');
    }

    let psbtB64 = unsignedB64;
    if (/^[0-9a-fA-F]+$/.test(unsignedB64)) {
      const bytes = new Uint8Array(
        unsignedB64.match(/.{1,2}/g).map((b) => parseInt(b, 16))
      );
      psbtB64 = btoa(
        Array.from(bytes)
          .map((byte) => String.fromCharCode(byte))
          .join('')
      );
    }

    let address, publicKey;
    try {
      address = deriveAddressFromPrivateKey(wallet.privateKey, network);
      publicKey = derivePublicKeyFromPrivateKey(wallet.privateKey, network);
    } catch (e) {
      address = wallet.address;
      publicKey = wallet.publicKey;
    }

    const signResult = await signPsbtWithProxyWallet(
      psbtB64.trim(),
      wallet.privateKey,
      network,
      {
        finalize: true,
        extractTx: true,
        expectedPublicKey: publicKey,
        walletAddress: address,
      }
    );

    if (!signResult?.hex) {
      throw new Error('Failed to sign dummy UTXO PSBT');
    }

    const broadcastRes = await fetch(getMempoolBroadcastUrl(network), {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: signResult.hex,
    });

    if (!broadcastRes.ok) {
      const errText = await broadcastRes.text();
      throw new Error(`Broadcast failed: ${broadcastRes.status} ${errText}`);
    }

    const txid = (await broadcastRes.text()).trim();
    log(`Dummy UTXO tx broadcasted: ${txid.slice(0, 16)}...`);

    const timeoutMs = 120000;
    const pollIntervalMs = 3000;
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      const confirmed = await checkTransactionConfirmed(txid, network);
      if (confirmed) {
        log(`Dummy UTXO tx confirmed: ${txid.slice(0, 16)}...`);
        return { success: true, txid };
      }
      await new Promise((r) => setTimeout(r, pollIntervalMs));
    }

    throw new Error(
      `Dummy UTXO tx not confirmed within ${timeoutMs / 1000}s: ${txid}`
    );
  } catch (err) {
    log(`Dummy UTXO error: ${err.message}`);
    return {
      success: false,
      error: err.message,
    };
  }
};

/** Helper: hex string to base64 for PSBT signing */
function hexToBase64(hexStr) {
  const bytes = new Uint8Array(
    hexStr.match(/.{1,2}/g).map((b) => parseInt(b, 16))
  );
  return btoa(
    Array.from(bytes)
      .map((byte) => String.fromCharCode(byte))
      .join('')
  );
}

/** Helper: base64 string to hex (broadcast API may expect hex for PSBTs) */
function base64ToHex(base64Str) {
  if (!base64Str || typeof base64Str !== 'string') return '';
  try {
    let b64 = base64Str.replace(/\s/g, '');
    const pad = b64.length % 4;
    if (pad) b64 += '='.repeat(4 - pad);
    const binary = atob(b64);
    return Array.from(binary)
      .map((c) => c.charCodeAt(0).toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return '';
  }
}

const SATFLOW_REFERRAL_ADDRESS = 'bc1q7dhu5ys8mp74re0zq0r7djjgpy4lw2q0u5049m';

/** Ensure PSBT string is in hex for broadcast (Satflow website sends hex). Pass-through if already hex. */
function toPsbtHexForBroadcast(psbtStr) {
  if (!psbtStr || typeof psbtStr !== 'string') return '';
  const s = psbtStr.trim();
  if (/^[0-9a-fA-F]+$/.test(s)) return s;
  return base64ToHex(s);
}

/** Helper: normalize PSBT string (hex or base64) to base64 */
function toPsbtBase64(psbtStr) {
  if (!psbtStr) return null;
  if (/^[0-9a-fA-F]+$/.test(psbtStr)) return hexToBase64(psbtStr);
  return psbtStr;
}

const SECURE_CONTEXT_MSG =
  'PSBT signing requires a secure context (HTTPS or localhost). Open the app via https:// or http://localhost (not plain HTTP on a remote host).';

/** Normalize errors that indicate missing secure context (browser or API). */
function normalizeSecureContextError(msg) {
  if (!msg || typeof msg !== 'string') return msg;
  const s = String(msg);
  if (/secure\s*context|crypto\.subtle|verifying\s*PSBT.*secure/i.test(s))
    return SECURE_CONTEXT_MSG;
  return msg;
}

/**
 * Secure purchase flow: do not broadcast until all 3 PSBTs are signed.
 * 1. GET PREP:       POST /v1/intent/secure-purchase (no signed prep) → paymentPrepPsbts
 * 2. SIGN PREP:      Sign prep PSBT (no broadcast)
 * 3. GET PURCHASE:   POST /v1/intent/secure-purchase with signedPaymentPrepPSBT → purchasePsbts
 * 4. SIGN PURCHASE:  Sign purchase PSBT(s)
 * 5. GET TRANSFER:   POST /v1/intent/secure-purchase with signed prep + signedPurchasePSBTs → transferPsbt
 * 6. SIGN TRANSFER:  Sign transfer PSBT
 * 7. BROADCAST:      POST /v1/purchase/broadcast once with all 3 (signed prep + signed purchase + signed transfer)
 *
 * prepareSecurePurchase = 1–2 (returns signedPaymentPrepPSBT only). completeSecurePurchase = 3–7.
 */

/**
 * Phase 1: Steps 1–2 — Get prep PSBT from intent/secure-purchase, then sign it (no broadcast). Store for complete step.
 * @returns {Promise<{ success: boolean, prepTxid?: string, noPrepNeeded?: boolean, intentData?: object, alreadyPrepared?: boolean, error?: string }>}
 */
export const prepareSecurePurchase = async (
  ordinal,
  wallet,
  network = 'mainnet',
  addConsoleLog = null,
  isStopRequested = null
) => {
  const tokenId = getTokenId(ordinal);
  if (!tokenId)
    return { success: false, error: 'Could not determine token ID' };
  if (!ordinal.listed || !ordinal.listedPrice)
    return { success: false, error: 'Waiting for item listing to propagate' };

  if (typeof isStopRequested === 'function' && isStopRequested()) {
    return { success: false, error: 'Trading stopped by user' };
  }

  let address, publicKey;
  try {
    address = deriveAddressFromPrivateKey(wallet.privateKey, network);
    publicKey = derivePublicKeyFromPrivateKey(wallet.privateKey, network);
  } catch (err) {
    address = wallet.address;
    publicKey = wallet.publicKey;
  }
  if (!address || !publicKey)
    return {
      success: false,
      error: 'Could not get wallet address or public key',
    };

  const intentPayload = {
    buyerAddress: address,
    buyerTokenReceiveAddress: address,
    buyerTokenReceivePublicKey: publicKey,
    buyerPublicKey: publicKey,
    inscriptionIds: [tokenId],
    feeRate: 4,
    referralAddress: SATFLOW_REFERRAL_ADDRESS,
    runesOutputs: [],
    disableCompactPurchase: true,
  };

  const runSecureIntent = async (signedPaymentPrepPSBTs = null) => {
    const body = { ...intentPayload };
    // Match working Satflow website: always send signedPaymentPrepPSBTs and signedPurchasePSBTs
    let prepsHex = [];
    if (signedPaymentPrepPSBTs && signedPaymentPrepPSBTs.length > 0) {
      prepsHex = signedPaymentPrepPSBTs
        .map((p) =>
          typeof p === 'string' ? p.trim() : String(p?.base64 ?? p?.hex ?? '')
        )
        .map((s) => toPsbtHexForBroadcast(s) || s)
        .filter((s) => s && typeof s === 'string');
    }
    body.signedPaymentPrepPSBTs = prepsHex;
    body.signedPurchasePSBTs = [];
    const res = await fetch('/api/satflow-intent-secure-purchase', {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok)
      return {
        ok: false,
        error: data?.error ?? data?.message ?? `HTTP ${res.status}`,
        data,
      };
    if (data?.success === false && data?.error)
      return { ok: false, error: data.error, data };
    return { ok: true, data };
  };

  // Do not use cached/stored prep — always get fresh prep PSBT from API (per user flow).
  // Step 1: Get prep PSBT from /v1/intent/secure-purchase (no signed prep in body)
  let intentResult = await runSecureIntent();
  if (
    !intentResult.ok &&
    String(intentResult.error || '').includes(SATFLOW_DUMMY_UTXO_ERROR)
  ) {
    if (typeof addConsoleLog === 'function')
      addConsoleLog('Additional dummy UTXOs required; creating dummy UTXO...');
    const dummyResult = await createDummyUtxoAndWait(
      address,
      wallet,
      network,
      addConsoleLog
    );
    if (!dummyResult.success)
      return {
        success: false,
        error: `Dummy UTXO failed: ${dummyResult.error}`,
      };
    intentResult = await runSecureIntent();
  }
  if (!intentResult.ok)
    return {
      success: false,
      error: intentResult.error || 'Secure purchase intent failed',
    };

  const intentData = intentResult.data?.data ?? intentResult.data;
  const paymentPrepPsbts = intentData?.paymentPrepPsbts ?? [];
  const purchasePsbts = intentData?.purchasePsbts ?? [];

  if (purchasePsbts.length > 0) {
    return { success: true, noPrepNeeded: true, intentData, intentPayload };
  }

  if (paymentPrepPsbts.length === 0) {
    return { success: false, error: 'No prep or purchase PSBTs returned' };
  }

  const prep = paymentPrepPsbts[0];
  const prepB64 = toPsbtBase64(prep.base64 ?? prep.hex);
  if (!prepB64)
    return { success: false, error: 'Payment prep PSBT missing base64/hex' };

  // Step 2: Sign the purchase prep PSBT (do not broadcast; pass signed prep to API in step 3)
  const prepSignResult = await signPsbtWithProxyWallet(
    prepB64.trim(),
    wallet.privateKey,
    network,
    {
      finalize: true,
      extractTx: true,
      expectedPublicKey: publicKey,
      walletAddress: address,
    }
  );
  if (!prepSignResult?.hex)
    return { success: false, error: 'Failed to sign payment prep PSBT' };
  const signedPaymentPrepPSBT =
    prepSignResult.base64 ?? toPsbtBase64(prepSignResult.hex);
  console.log(
    '[Satflow] signed payment prep PSBT (base64):',
    signedPaymentPrepPSBT
  );

  // Do not broadcast prep; broadcast only once with all 3 PSBTs (prep + purchase + transfer) via purchase/broadcast
  if (typeof addConsoleLog === 'function') {
    addConsoleLog(
      `Payment prep PSBT signed (no broadcast); pass to complete to get purchase + transfer, then broadcast all.`
    );
  }
  return { success: true, signedPaymentPrepPSBT };
};

/**
 * Complete secure purchase. Flow (no cached PSBTs):
 * 1. Get prep PSBT (intent, no signed prep) — or use options.signedPaymentPrepPSBT from same-run prepare
 * 2. Sign prep PSBT (if not from options)
 * 3. Get purchase PSBT (intent with signed prep)
 * 4. Sign purchase PSBT(s)
 * 5. Get transfer PSBT (intent with signed prep + signed purchase)
 * 6. Sign transfer PSBT, then broadcast via /v1/purchase/broadcast
 * @param {Object} options.intentData - Optional: if intent already returned purchasePsbts (no-prep path)
 * @param {string} options.signedPaymentPrepPSBT - Optional: same-run signed prep (do not use cached/stored)
 */
export const completeSecurePurchase = async (
  ordinal,
  wallet,
  network = 'mainnet',
  addConsoleLog = null,
  isStopRequested = null,
  options = {}
) => {
  const tokenId = getTokenId(ordinal);
  if (!tokenId)
    return { success: false, error: 'Could not determine token ID' };
  const tokenIdShort = tokenId.slice(0, 8);
  if (typeof addConsoleLog === 'function')
    addConsoleLog(`  Complete purchase for item ${tokenIdShort}...`);

  let address, publicKey;
  try {
    address = deriveAddressFromPrivateKey(wallet.privateKey, network);
    publicKey = derivePublicKeyFromPrivateKey(wallet.privateKey, network);
  } catch (err) {
    address = wallet.address;
    publicKey = wallet.publicKey;
  }
  if (!address || !publicKey)
    return {
      success: false,
      error: 'Could not get wallet address or public key',
    };

  const intentPayload = {
    buyerAddress: address,
    buyerTokenReceiveAddress: address,
    buyerTokenReceivePublicKey: publicKey,
    buyerPublicKey: publicKey,
    inscriptionIds: [tokenId],
    feeRate: 4,
    referralAddress: SATFLOW_REFERRAL_ADDRESS,
    runesOutputs: [],
    disableCompactPurchase: true,
  };

  const runSecureIntent = async (
    signedPaymentPrepPSBTs = null,
    signedPurchasePSBTs = null
  ) => {
    const body = { ...intentPayload };
    // Match working Satflow website: always send signedPaymentPrepPSBTs and signedPurchasePSBTs (arrays of hex strings)
    let prepsHex = [];
    if (
      signedPaymentPrepPSBTs &&
      Array.isArray(signedPaymentPrepPSBTs) &&
      signedPaymentPrepPSBTs.length > 0
    ) {
      prepsHex = signedPaymentPrepPSBTs
        .map((p) =>
          typeof p === 'string' ? p.trim() : String(p?.base64 ?? p?.hex ?? '')
        )
        .map((s) => toPsbtHexForBroadcast(s) || s)
        .filter((s) => s && typeof s === 'string');
    }
    body.signedPaymentPrepPSBTs = prepsHex;

    let purchasesHex = [];
    if (
      signedPurchasePSBTs &&
      Array.isArray(signedPurchasePSBTs) &&
      signedPurchasePSBTs.length > 0
    ) {
      purchasesHex = signedPurchasePSBTs
        .map((p) =>
          typeof p === 'string' ? p.trim() : String(p?.base64 ?? p?.hex ?? '')
        )
        .map((s) => toPsbtHexForBroadcast(s) || s)
        .filter((s) => s && typeof s === 'string');
    }
    body.signedPurchasePSBTs = purchasesHex;

    const res = await fetch('/api/satflow-intent-secure-purchase', {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok)
      return { ok: false, error: data?.error ?? data?.message, data };
    if (data?.success === false && data?.error)
      return { ok: false, error: data.error, data };
    return { ok: true, data };
  };

  // Flow: Get prep → Sign prep → Get purchase → Sign purchase → Get transfer → Sign transfer → Broadcast.
  // Do not use cached/stored PSBTs; use only same-run signed prep (options.signedPaymentPrepPSBT) or fetch fresh.
  let signedPaymentPrepPSBT = options.signedPaymentPrepPSBT ?? null;
  let purchaseList = [];

  if (
    options.intentData &&
    (options.intentData.purchasePsbts ?? []).length > 0
  ) {
    purchaseList = options.intentData.purchasePsbts ?? [];
  } else {
    // Need signed prep: use same-run prep from options, or get prep + sign fresh (no cache).
    if (!signedPaymentPrepPSBT) {
      if (typeof addConsoleLog === 'function')
        addConsoleLog('  Get prep PSBT (intent, no signed prep)...');
      const prepIntentResult = await runSecureIntent();
      if (!prepIntentResult.ok)
        return {
          success: false,
          error:
            normalizeSecureContextError(prepIntentResult.error) ||
            prepIntentResult.error ||
            'Secure purchase (get prep) failed',
        };
      const prepIntentData =
        prepIntentResult.data?.data ?? prepIntentResult.data;
      const paymentPrepPsbts = prepIntentData?.paymentPrepPsbts ?? [];
      const prep = paymentPrepPsbts[0];
      if (!prep) {
        const purchaseFromPrep =
          prepIntentData?.purchasePsbts ?? prepIntentData?.purchasepsbts ?? [];
        if (purchaseFromPrep.length > 0) {
          purchaseList = purchaseFromPrep;
        }
        if (purchaseList.length === 0)
          return {
            success: false,
            error: 'No prep or purchase PSBTs returned from intent',
          };
      } else {
        const prepB64 = toPsbtBase64(prep.base64 ?? prep.hex);
        if (!prepB64)
          return {
            success: false,
            error: 'Payment prep PSBT missing base64/hex',
          };
        if (typeof addConsoleLog === 'function')
          addConsoleLog('  Sign prep PSBT...');
        const prepSignResult = await signPsbtWithProxyWallet(
          prepB64.trim(),
          wallet.privateKey,
          network,
          {
            finalize: true,
            extractTx: true,
            expectedPublicKey: publicKey,
            walletAddress: address,
          }
        );
        if (!prepSignResult?.hex)
          return { success: false, error: 'Failed to sign payment prep PSBT' };
        signedPaymentPrepPSBT =
          prepSignResult.base64 ?? toPsbtBase64(prepSignResult.hex);
        console.log(
          '[Satflow] signed payment prep PSBT (base64):',
          signedPaymentPrepPSBT
        );
      }
    }
    if (purchaseList.length === 0 && signedPaymentPrepPSBT) {
      const extractPurchaseList = (raw) => {
        const d = raw?.data ?? raw;
        return (
          d?.purchasePsbts ??
          d?.purchasepsbts ??
          (Array.isArray(d?.purchasePsbt) ? d.purchasePsbt : null) ??
          []
        );
      };
      const retryDelays = [0, 10000, 20000];
      for (let attempt = 0; attempt < retryDelays.length; attempt++) {
        if (typeof isStopRequested === 'function' && isStopRequested())
          return { success: false, error: 'Trading stopped by user' };
        if (retryDelays[attempt] > 0 && typeof addConsoleLog === 'function') {
          addConsoleLog(
            `  Get purchase PSBT retry ${attempt + 1}/${retryDelays.length} in ${retryDelays[attempt] / 1000}s (backend may need time after prep confirm)...`
          );
          await new Promise((r) => setTimeout(r, retryDelays[attempt]));
        }
        if (typeof addConsoleLog === 'function')
          addConsoleLog('  Get purchase PSBT (intent with signed prep)...');
        const purchaseIntentResult = await runSecureIntent([
          signedPaymentPrepPSBT,
        ]);
        if (!purchaseIntentResult.ok)
          return {
            success: false,
            error:
              normalizeSecureContextError(purchaseIntentResult.error) ||
              purchaseIntentResult.error ||
              'Secure purchase (get purchase PSBT) failed',
          };
        const purchaseData =
          purchaseIntentResult.data?.data ?? purchaseIntentResult.data;
        purchaseList = extractPurchaseList(purchaseIntentResult.data);
        if (!Array.isArray(purchaseList)) purchaseList = [];
        if (purchaseList.length > 0) {
          console.log(
            '[Satflow] received purchase PSBTs from API:',
            purchaseList.length
          );
          // Log all top-level keys from intent response (helps debug "Mismatched compact txs" – look for compactPurchasePsbts etc.)
          const dataObj =
            purchaseIntentResult.data?.data ?? purchaseIntentResult.data ?? {};
          console.log('[Satflow] intent response keys:', Object.keys(dataObj));
          break;
        }
        if (
          attempt < retryDelays.length - 1 &&
          typeof addConsoleLog === 'function'
        )
          addConsoleLog(
            `  No purchase PSBTs in response (keys: ${Object.keys(purchaseData || {}).join(', ')})`
          );
      }
    }
    if (purchaseList.length === 0)
      return {
        success: false,
        error:
          'No purchase PSBTs returned from intent after retries (ensure prep tx confirmed and same-run signed prep used)',
      };
  }

  // Step 4: Sign the purchase PSBT(s). Do not finalize — broadcast API expects signed PSBTs and finalizes server-side (finalize: true causes "unknown input" for some purchase PSBTs).
  const signedPurchasePSBTs = [];
  try {
    for (let i = 0; i < purchaseList.length; i++) {
      const p = purchaseList[i];
      const pB64 = toPsbtBase64(p.base64 ?? p.hex);
      if (!pB64) continue;
      const signP = await signPsbtWithProxyWallet(
        pB64.trim(),
        wallet.privateKey,
        network,
        {
          finalize: false,
          extractTx: false,
          expectedPublicKey: publicKey,
          walletAddress: address,
        }
      );
      if (signP?.base64) signedPurchasePSBTs.push(signP.base64);
    }
  } catch (signErr) {
    const msg = signErr && signErr.message ? String(signErr.message) : '';
    return {
      success: false,
      error:
        normalizeSecureContextError(msg) ||
        msg ||
        'Failed to sign purchase PSBTs',
    };
  }

  if (signedPurchasePSBTs.length === 0) {
    return {
      success: false,
      error:
        'No purchase PSBTs to sign; secure purchase flow may be incomplete',
    };
  }

  console.log(
    '[Satflow] signed purchase PSBT(s) (base64):',
    signedPurchasePSBTs.length === 1
      ? signedPurchasePSBTs[0]
      : signedPurchasePSBTs
  );

  // Step 3b: Get transfer PSBT from /v1/intent/secure-purchase with signedPaymentPrepPSBTs + signedPurchasePSBTs
  const signedPaymentPrepPSBTsForIntent = signedPaymentPrepPSBT
    ? [signedPaymentPrepPSBT]
    : [];
  const transferIntentResult = await runSecureIntent(
    signedPaymentPrepPSBTsForIntent,
    signedPurchasePSBTs
  );
  let signedSecureTransferPSBT = null;
  if (transferIntentResult.ok) {
    const transferData =
      transferIntentResult.data?.data ?? transferIntentResult.data;
    const transferPsbtObj =
      transferData?.transferPsbt ?? transferData?.transferPsbts?.[0];
    const tB64 = transferPsbtObj
      ? toPsbtBase64(
          transferPsbtObj.base64 ?? transferPsbtObj.hex ?? transferPsbtObj
        )
      : null;
    if (tB64) {
      if (typeof addConsoleLog === 'function')
        addConsoleLog('  Signing transfer PSBT...');
      try {
        const signT = await signPsbtWithProxyWallet(
          tB64.trim(),
          wallet.privateKey,
          network,
          { finalize: false, extractTx: false, walletAddress: address }
        );
        if (signT?.base64) {
          signedSecureTransferPSBT = signT.base64;
          if (typeof addConsoleLog === 'function')
            addConsoleLog('  Transfer PSBT signed.');
        }
      } catch (e) {
        console.warn('[Satflow] transfer PSBT sign failed:', e?.message);
        if (typeof addConsoleLog === 'function')
          addConsoleLog(`  ⚠ Transfer sign failed: ${e?.message}`);
      }
    }
  } else if (typeof addConsoleLog === 'function') {
    addConsoleLog(
      `  ⚠ Could not get transfer PSBT: ${transferIntentResult.error || 'unknown'}`
    );
  }

  // Build broadcast payload per POST /v1/purchase/broadcast. Strict 1:1 to avoid "Mismatched compact txs":
  // inscriptionIds.length === signedSecurePaymentPrepPSBTs.length === signedSecurePurchasePSBTs.length (all arrays of strings).
  const inscriptionIds = [tokenId];
  const n = inscriptionIds.length;

  const prepStr =
    signedPaymentPrepPSBT != null
      ? typeof signedPaymentPrepPSBT === 'string'
        ? signedPaymentPrepPSBT.trim()
        : String(
            signedPaymentPrepPSBT?.base64 ?? signedPaymentPrepPSBT?.hex ?? ''
          ).trim()
      : '';
  const prepList = prepStr.length > 0 ? [prepStr] : [];
  const purchaseListForBroadcast = (
    Array.isArray(signedPurchasePSBTs)
      ? signedPurchasePSBTs
      : [signedPurchasePSBTs].filter(Boolean)
  )
    .slice(0, n)
    .map((p) =>
      (typeof p === 'string' ? p : String(p?.base64 ?? p?.hex ?? '')).trim()
    )
    .filter((s) => s.length > 0);

  if (purchaseListForBroadcast.length !== n) {
    return {
      success: false,
      error: `PSBT/inscription count mismatch: ${purchaseListForBroadcast.length} purchase PSBT(s) for ${n} inscription(s). Each ordinal must have one prep and one purchase PSBT.`,
    };
  }
  if (prepList.length > 0 && prepList.length !== n) {
    return {
      success: false,
      error: `Prep PSBT count (${prepList.length}) must match inscription count (${n}).`,
    };
  }
  if (
    signedSecureTransferPSBT &&
    (prepList.length !== n || purchaseListForBroadcast.length !== n)
  ) {
    return {
      success: false,
      error: `For full secure flow (with transfer), prep and purchase counts must equal inscription count (${n}). Got prep: ${prepList.length}, purchase: ${purchaseListForBroadcast.length}.`,
    };
  }
  if (signedSecureTransferPSBT && prepList.length === 0) {
    return {
      success: false,
      error:
        'Broadcast requires a signed payment prep PSBT for secure purchase (same run as purchase/transfer). Missing prep.',
    };
  }

  const transferStr =
    signedSecureTransferPSBT != null
      ? typeof signedSecureTransferPSBT === 'string'
        ? signedSecureTransferPSBT.trim()
        : String(
            signedSecureTransferPSBT?.base64 ??
              signedSecureTransferPSBT?.hex ??
              ''
          ).trim()
      : '';

  // Match working Satflow website payload: all three PSBTs in HEX, prep included (same format as their proxy).
  const prepTrimmed =
    prepList.length === n
      ? prepList.map((p) =>
          typeof p === 'string' ? p.trim() : String(p ?? '')
        )
      : [];
  const purchaseTrimmed = purchaseListForBroadcast.map((p) =>
    typeof p === 'string' ? p.trim() : String(p ?? '')
  );
  const transferTrimmed = transferStr
    ? typeof transferStr === 'string'
      ? transferStr.trim()
      : String(transferStr ?? '')
    : '';

  const prepHex =
    prepTrimmed.length > 0
      ? prepTrimmed.map((p) => toPsbtHexForBroadcast(p) || p)
      : [];
  const purchaseHex = purchaseTrimmed.map((p) => toPsbtHexForBroadcast(p) || p);
  const transferHex = transferTrimmed
    ? toPsbtHexForBroadcast(transferTrimmed) || transferTrimmed
    : '';

  const broadcastPayload = {
    buyerAddress: address,
    buyerTokenReceiveAddress: address,
    buyerTokenReceivePublicKey: publicKey,
    buyerPublicKey: publicKey,
    feeRate: 4,
    referralAddress: SATFLOW_REFERRAL_ADDRESS,
    inscriptionIds,
    runesOutputs: [],
    extractionFeeRate: 0,
    signedSecurePaymentPrepPSBTs: prepHex,
    signedSecurePurchasePSBTs: purchaseHex,
    splitQuantity: 0,
    securePurchase: true,
    skipBroadcast: false,
  };
  if (transferHex) {
    broadcastPayload.signedSecureTransferPSBT = transferHex;
  }

  console.log('[Satflow] broadcast payload (hex PSBTs, prep included):', {
    inscriptionIds: inscriptionIds.length,
    signedSecurePaymentPrepPSBTs:
      broadcastPayload.signedSecurePaymentPrepPSBTs?.length ?? 0,
    signedSecurePurchasePSBTs:
      broadcastPayload.signedSecurePurchasePSBTs?.length ?? 0,
    hasTransfer: Boolean(broadcastPayload.signedSecureTransferPSBT),
  });

  // Step 5: Broadcast via POST /v1/purchase/broadcast (retry on 500 "Missing secure context" in case it's transient)
  const BROADCAST_RETRIES = 3;
  const BROADCAST_RETRY_DELAY_MS = 2500;
  let broadcastRes;
  let rawText;
  let broadcastData;
  for (let attempt = 1; attempt <= BROADCAST_RETRIES; attempt++) {
    if (attempt > 1 && typeof addConsoleLog === 'function')
      addConsoleLog(`  Broadcast retry ${attempt}/${BROADCAST_RETRIES}...`);
    broadcastRes = await fetch('/api/satflow-purchase-broadcast', {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
      },
      body: JSON.stringify(broadcastPayload),
    });
    rawText = await broadcastRes.text();
    try {
      broadcastData = rawText ? JSON.parse(rawText) : {};
    } catch (parseErr) {
      console.error(
        '[Satflow] purchase/broadcast response not JSON:',
        broadcastRes.status,
        rawText?.slice(0, 500)
      );
      return {
        success: false,
        error: `Broadcast failed (${broadcastRes.status}): ${rawText?.slice(0, 200) || 'Invalid response'}`,
      };
    }
    const fillTx = broadcastData?.data?.fillTx ?? broadcastData?.fillTx;
    if (broadcastRes.ok && (broadcastData?.success === true || fillTx)) {
      removePendingPrep(tokenId, address);
      return { success: true, txid: fillTx || null, data: broadcastData };
    }
    const apiError =
      broadcastData?.error ??
      broadcastData?.message ??
      (typeof broadcastData?.data === 'string' ? broadcastData.data : null) ??
      `HTTP ${broadcastRes.status}`;
    const isRetryable500 =
      broadcastRes.status === 500 &&
      /verifying PSBT|Missing secure context/i.test(String(apiError));
    if (!isRetryable500 || attempt === BROADCAST_RETRIES) break;
    await new Promise((r) => setTimeout(r, BROADCAST_RETRY_DELAY_MS));
  }

  const fillTx = broadcastData?.data?.fillTx ?? broadcastData?.fillTx;
  if (broadcastRes.ok && (broadcastData?.success === true || fillTx)) {
    removePendingPrep(tokenId, address);
    return { success: true, txid: fillTx || null, data: broadcastData };
  }

  const apiError =
    broadcastData?.error ??
    broadcastData?.message ??
    (typeof broadcastData?.data === 'string' ? broadcastData.data : null) ??
    `HTTP ${broadcastRes.status}`;
  console.error(
    '[Satflow] purchase/broadcast failed (step 5):',
    broadcastRes.status,
    apiError,
    broadcastData
  );
  const isSatflowServerSecureContext =
    broadcastRes.status === 500 &&
    /verifying PSBT|Missing secure context/i.test(String(apiError));
  if (isSatflowServerSecureContext) {
    console.warn(
      '[Satflow] Broadcast API 500 "Missing secure context" — Satflow server-side bug. Report to Satflow Discord: POST /v1/purchase/broadcast returns 500 with this error.'
    );
  }
  const displayError = isSatflowServerSecureContext
    ? "Satflow's broadcast API is returning a server error (500 - Missing secure context). This is a bug on Satflow's side, not your app. Please report to Satflow support (Discord) and retry later."
    : apiError;
  return {
    success: false,
    error: displayError,
    _broadcastFailed: true,
  };
};

/**
 * Buy an ordinal using proxy wallet (Satflow secure-purchase).
 * Signed payment prep PSBT is passed to the API (not broadcast by us); API returns purchase PSBTs.
 * Supports batched flow: pass skipWaitForPrep: true to only prepare, then caller calls completeSecurePurchase.
 */
export const buyOrdinalWithProxyWallet = async (
  ordinal,
  wallet,
  network = 'mainnet',
  connectedWallet = 'unisat',
  addConsoleLog = null,
  isStopRequested = null,
  opts = {}
) => {
  const skipWaitForPrep = opts.skipWaitForPrep === true;
  try {
    if (typeof addConsoleLog === 'function') {
      addConsoleLog(`Using connected wallet provider: ${connectedWallet}`);
    }
    if (typeof isStopRequested === 'function' && isStopRequested()) {
      return { success: false, error: 'Trading stopped by user' };
    }
    const tokenId = getTokenId(ordinal);
    if (!tokenId)
      return {
        success: false,
        error: 'Could not determine token ID from selected ordinal',
      };
    if (!ordinal.listed || !ordinal.listedPrice)
      return { success: false, error: 'Waiting for item listing to propagate' };

    const prepareResult = await prepareSecurePurchase(
      ordinal,
      wallet,
      network,
      addConsoleLog,
      isStopRequested
    );
    if (!prepareResult.success)
      return { success: false, error: prepareResult.error };

    if (prepareResult.noPrepNeeded && prepareResult.intentData) {
      return await completeSecurePurchase(
        ordinal,
        wallet,
        network,
        addConsoleLog,
        isStopRequested,
        {
          intentData: prepareResult.intentData,
        }
      );
    }

    if (prepareResult.prepTxid && skipWaitForPrep) {
      return {
        success: true,
        prepTxid: prepareResult.prepTxid,
        waitThenComplete: true,
        message:
          'Prep broadcast; wait for confirmation then call completeSecurePurchase',
      };
    }

    if (prepareResult.prepTxid) {
      if (typeof addConsoleLog === 'function') {
        addConsoleLog('  Waiting for prep tx to confirm (up to 60 min)...');
      }
      await waitForPrepsToConfirm([prepareResult.prepTxid], {
        timeoutMs: PREP_CONFIRM_TIMEOUT_MS,
        addConsoleLog,
        isStopRequested,
        network,
      });
      if (typeof isStopRequested === 'function' && isStopRequested()) {
        return { success: false, error: 'Trading stopped by user' };
      }
      if (typeof addConsoleLog === 'function') {
        addConsoleLog('  Waiting for backend to index prep tx (15s)...');
      }
      await new Promise((r) => setTimeout(r, 15000));
    }

    const completeOpts = {};
    if (prepareResult.signedPaymentPrepPSBT) {
      completeOpts.signedPaymentPrepPSBT = prepareResult.signedPaymentPrepPSBT;
    }

    return await completeSecurePurchase(
      ordinal,
      wallet,
      network,
      addConsoleLog,
      isStopRequested,
      completeOpts
    );
  } catch (err) {
    console.error('Error buying ordinal:', err);
    return { success: false, error: err.message || 'Failed to buy ordinal' };
  }
};

/**
 * Send trading fee (1% of purchase price) to fee receiver address
 * Uses the change output from the purchase transaction to avoid UTXO conflicts
 * @param {Object} wallet - Proxy wallet object
 * @param {number} purchasePrice - Purchase price in satoshis
 * @param {string} purchaseTxid - Purchase transaction ID
 * @param {string} network - Network type
 * @param {Function} addConsoleLog - Logging function
 * @returns {Promise<Object>} Result with success, txid, error
 */
export const sendTradingFee = async (
  wallet,
  purchasePrice,
  purchaseTxid,
  network = 'mainnet',
  addConsoleLog = null
) => {
  const FEE_RECEIVER_ADDRESS = 'bc1q7dhu5ys8mp74re0zq0r7djjgpy4lw2q0u5049m';
  const FEE_PERCENTAGE = 0.01; // 1%

  try {
    // Calculate fee amount (1% of purchase price)
    const feeAmount = Math.floor(purchasePrice * FEE_PERCENTAGE);

    if (feeAmount <= 0) {
      const errorMsg = 'Fee amount is zero or negative';
      if (addConsoleLog) {
        addConsoleLog(`⚠ Fee transaction skipped: ${errorMsg}`);
      }
      return {
        success: false,
        error: errorMsg,
      };
    }

    if (!purchaseTxid) {
      const errorMsg = 'Purchase transaction ID is required';
      if (addConsoleLog) {
        addConsoleLog(`⚠ Fee transaction skipped: ${errorMsg}`);
      }
      return {
        success: false,
        error: errorMsg,
      };
    }

    // Derive wallet address and public key
    let address, publicKey;
    try {
      address = deriveAddressFromPrivateKey(wallet.privateKey, network);
      publicKey = derivePublicKeyFromPrivateKey(wallet.privateKey, network);
    } catch (err) {
      console.error('Error deriving address from private key:', err);
      address = wallet.address;
      publicKey = wallet.publicKey;
    }

    if (!address || !publicKey) {
      throw new Error('Could not get wallet address or public key');
    }

    if (addConsoleLog) {
      addConsoleLog(
        `💰 Creating fee transaction: ${(feeAmount / 100000000).toFixed(8)} BTC (1% of ${(purchasePrice / 100000000).toFixed(8)} BTC)`
      );
      addConsoleLog(
        `  Using change from purchase tx: ${purchaseTxid.slice(0, 16)}...`
      );
    }

    // Query the purchase transaction to find the change output
    let purchaseTx = null;
    let retries = 0;
    const maxRetries = 5;
    const retryDelay = 2000; // 2 seconds

    while (retries < maxRetries && !purchaseTx) {
      try {
        const txResponse = await fetch(
          getMempoolTxUrl(purchaseTxid, network || 'mainnet')
        );

        if (txResponse.ok) {
          purchaseTx = await txResponse.json();
          break;
        } else if (txResponse.status === 404) {
          // Transaction not found yet, wait and retry
          retries++;
          if (retries < maxRetries) {
            if (addConsoleLog) {
              addConsoleLog(
                `  ⏳ Purchase transaction not found yet, retrying in ${retryDelay / 1000}s... (${retries}/${maxRetries})`
              );
            }
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
            continue;
          } else {
            throw new Error('Purchase transaction not found after retries');
          }
        } else {
          throw new Error(
            `Failed to fetch purchase transaction: ${txResponse.status}`
          );
        }
      } catch (err) {
        if (retries >= maxRetries) {
          throw err;
        }
        retries++;
        if (retries < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
        }
      }
    }

    if (!purchaseTx || !purchaseTx.vout || purchaseTx.vout.length === 0) {
      throw new Error('Purchase transaction has no outputs');
    }

    // Find the change output (output that goes back to the buyer's address)
    const changeOutput = purchaseTx.vout.find(
      (output) =>
        output.scriptpubkey_address &&
        output.scriptpubkey_address.toLowerCase() === address.toLowerCase()
    );

    // Estimate transaction fee (needed for validation)
    const TX_BASE = 10;
    const P2TR_IN_VB = 58;
    const P2TR_OUT_VB = 43;
    const FEE_RATE = 'hourFee';
    const estimatedTxFee = Math.ceil(
      FEE_RATE * (TX_BASE + P2TR_IN_VB + P2TR_OUT_VB * 2)
    );
    const totalNeeded = feeAmount + estimatedTxFee;

    let changeUtxo = null;

    if (!changeOutput) {
      // If no exact match, try to find the largest output that might be change
      const sortedOutputs = [...purchaseTx.vout].sort(
        (a, b) => (b.value || 0) - (a.value || 0)
      );
      const largestOutput = sortedOutputs[0];

      if (largestOutput && largestOutput.value >= totalNeeded) {
        // Use largest output if it's large enough
        if (addConsoleLog) {
          addConsoleLog(
            `  ⚠ No exact address match found, using largest output (${largestOutput.scriptpubkey_address?.slice(0, 16)}...)`
          );
        }
        const changeOutputIndex = purchaseTx.vout.indexOf(largestOutput);
        changeUtxo = {
          txid: purchaseTxid,
          vout: changeOutputIndex,
          value: largestOutput.value,
          scriptpubkey: largestOutput.scriptpubkey,
          scriptpubkey_address: largestOutput.scriptpubkey_address,
          status: purchaseTx.status || { confirmed: false },
        };
      } else {
        throw new Error(
          `No suitable change output found. Largest output: ${(largestOutput?.value || 0) / 100000000} BTC, needed: ${(totalNeeded / 100000000).toFixed(8)} BTC`
        );
      }
    } else {
      // Found exact match - use the change output
      const changeOutputIndex = purchaseTx.vout.indexOf(changeOutput);
      changeUtxo = {
        txid: purchaseTxid,
        vout: changeOutputIndex,
        value: changeOutput.value,
        scriptpubkey: changeOutput.scriptpubkey,
        scriptpubkey_address: changeOutput.scriptpubkey_address,
        status: purchaseTx.status || { confirmed: false },
      };

      if (addConsoleLog) {
        addConsoleLog(
          `  ✓ Found change output: ${(changeUtxo.value / 100000000).toFixed(8)} BTC at vout ${changeOutputIndex}`
        );
      }
    }

    // Validate change output is large enough
    if (changeUtxo.value < totalNeeded) {
      throw new Error(
        `Change output (${(changeUtxo.value / 100000000).toFixed(8)} BTC) is too small for fee (${(feeAmount / 100000000).toFixed(8)} BTC + ${(estimatedTxFee / 100000000).toFixed(8)} BTC fees)`
      );
    }

    // Use the change UTXO
    const selectedUtxos = [changeUtxo];
    const totalSelected = changeUtxo.value;

    // Create PSBT
    const { Psbt, networks, payments } = bitcoin;
    const networkConfig =
      network === 'mainnet' ? networks.bitcoin : networks.testnet;
    const psbt = new Psbt({ network: networkConfig });

    // Detect address format
    const detectAddressFormat = (addr) => {
      if (addr.startsWith('bc1p') || addr.startsWith('tb1p')) return 'p2tr';
      if (
        (addr.startsWith('bc1') && addr.length === 42) ||
        (addr.startsWith('tb1') && addr.length === 42)
      )
        return 'p2wpkh';
      if (addr.startsWith('1') || addr.startsWith('m') || addr.startsWith('n'))
        return 'p2pkh';
      if (addr.startsWith('3') || addr.startsWith('2')) return 'p2sh';
      return 'p2tr'; // Default to P2TR
    };

    // Detect address format from the change output (more reliable than wallet address)
    const changeAddressFormat = changeUtxo.scriptpubkey_address
      ? detectAddressFormat(changeUtxo.scriptpubkey_address)
      : detectAddressFormat(address);

    // Add inputs
    for (const utxo of selectedUtxos) {
      const inputData = {
        hash: utxo.txid,
        index: utxo.vout,
        sequence: 0xffffffff,
      };

      // Use the scriptpubkey from the transaction output if available
      if (utxo.scriptpubkey) {
        try {
          const scriptBuffer = Buffer.from(utxo.scriptpubkey, 'hex');

          // Determine if this is a SegWit output based on scriptpubkey
          // P2TR: starts with 0x51 (OP_1) followed by 0x20 (32 bytes)
          // P2WPKH: starts with 0x00 (OP_0) followed by 0x14 (20 bytes)
          // P2PKH: starts with 0x76 (OP_DUP)
          const scriptHex = utxo.scriptpubkey;

          if (scriptHex.startsWith('5120')) {
            // P2TR (Taproot) - use witnessUtxo
            inputData.witnessUtxo = {
              script: new Uint8Array(scriptBuffer),
              value: BigInt(utxo.value),
            };
          } else if (scriptHex.startsWith('0014')) {
            // P2WPKH - use witnessUtxo
            inputData.witnessUtxo = {
              script: new Uint8Array(scriptBuffer),
              value: BigInt(utxo.value),
            };
          } else if (scriptHex.startsWith('76')) {
            // P2PKH - need full transaction
            const txResponse = await fetch(
              getMempoolTxHexUrl(utxo.txid, network || 'mainnet')
            );
            if (txResponse.ok) {
              const txHex = await txResponse.text();
              const txBuffer = Buffer.from(txHex, 'hex');
              inputData.nonWitnessUtxo = txBuffer;
            } else {
              throw new Error(
                `Failed to fetch transaction ${utxo.txid} for P2PKH input`
              );
            }
          } else {
            // Default to witnessUtxo for other SegWit types
            inputData.witnessUtxo = {
              script: new Uint8Array(scriptBuffer),
              value: BigInt(utxo.value),
            };
          }
        } catch (e) {
          // Fallback: create from public key based on address format
          console.warn(
            'Error using scriptpubkey, falling back to public key:',
            e
          );
          const pubkeyBuffer = Buffer.from(publicKey, 'hex');
          let payment;
          if (changeAddressFormat === 'p2tr') {
            const xOnlyPubkey = pubkeyBuffer.slice(1, 33);
            payment = payments.p2tr({
              internalPubkey: xOnlyPubkey,
              network: networkConfig,
            });
          } else if (changeAddressFormat === 'p2pkh') {
            // For P2PKH, need full transaction
            const txResponse = await fetch(
              getMempoolTxHexUrl(utxo.txid, network || 'mainnet')
            );
            if (txResponse.ok) {
              const txHex = await txResponse.text();
              const txBuffer = Buffer.from(txHex, 'hex');
              inputData.nonWitnessUtxo = txBuffer;
            } else {
              throw new Error(
                `Failed to fetch transaction ${utxo.txid} for P2PKH input`
              );
            }
            psbt.addInput(inputData);
            continue; // Skip to next iteration
          } else {
            payment = payments.p2wpkh({
              pubkey: pubkeyBuffer,
              network: networkConfig,
            });
          }
          if (payment) {
            inputData.witnessUtxo = {
              script: new Uint8Array(payment.output),
              value: BigInt(utxo.value),
            };
          }
        }
      } else {
        // No scriptpubkey - create from public key
        const pubkeyBuffer = Buffer.from(publicKey, 'hex');
        let payment;
        if (changeAddressFormat === 'p2tr') {
          const xOnlyPubkey = pubkeyBuffer.slice(1, 33);
          payment = payments.p2tr({
            internalPubkey: xOnlyPubkey,
            network: networkConfig,
          });
        } else if (changeAddressFormat === 'p2pkh') {
          // For P2PKH, need full transaction
          const txResponse = await fetch(
            getMempoolTxHexUrl(utxo.txid, network || 'mainnet')
          );
          if (txResponse.ok) {
            const txHex = await txResponse.text();
            const txBuffer = Buffer.from(txHex, 'hex');
            inputData.nonWitnessUtxo = txBuffer;
          } else {
            throw new Error(
              `Failed to fetch transaction ${utxo.txid} for P2PKH input`
            );
          }
          psbt.addInput(inputData);
          continue; // Skip to next iteration
        } else {
          payment = payments.p2wpkh({
            pubkey: pubkeyBuffer,
            network: networkConfig,
          });
        }
        if (payment) {
          inputData.witnessUtxo = {
            script: new Uint8Array(payment.output),
            value: BigInt(utxo.value),
          };
        }
      }

      psbt.addInput(inputData);
    }

    // Add fee receiver output
    psbt.addOutput({
      address: FEE_RECEIVER_ADDRESS,
      value: BigInt(feeAmount),
    });

    // Calculate and add change output if needed
    const change = totalSelected - feeAmount - estimatedTxFee;
    const DUST_THRESHOLD = 300;
    if (change >= DUST_THRESHOLD) {
      psbt.addOutput({
        address: address,
        value: BigInt(change),
      });
    }

    // Sign PSBT
    const psbtBase64 = psbt.toBase64();
    const signResult = await signPsbtWithProxyWallet(
      psbtBase64,
      wallet.privateKey,
      network,
      {
        finalize: true,
        extractTx: true,
        expectedPublicKey: publicKey,
        walletAddress: address,
      }
    );

    if (!signResult || !signResult.hex) {
      throw new Error('Failed to sign fee transaction PSBT');
    }

    const signedTxHex = signResult.hex;

    // Broadcast transaction
    const broadcastResponse = await fetch(
      getMempoolBroadcastUrl(network || 'mainnet'),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: signedTxHex,
      }
    );

    if (!broadcastResponse.ok) {
      const errorText = await broadcastResponse.text();
      throw new Error(
        `Failed to broadcast fee transaction: ${broadcastResponse.status} - ${errorText}`
      );
    }

    const txId = await broadcastResponse.text();

    if (addConsoleLog) {
      const txLink = getMempoolTxUrl(txId, network || 'mainnet');
      addConsoleLog(
        `✓ Fee transaction broadcasted! Amount: ${(feeAmount / 100000000).toFixed(8)} BTC (1% of ${(purchasePrice / 100000000).toFixed(8)} BTC), Tx: ${txId.slice(0, 16)}...`,
        txLink
      );
      addConsoleLog(
        `  Fee Details: ${(feeAmount / 100000000).toFixed(8)} BTC → ${FEE_RECEIVER_ADDRESS.slice(0, 16)}...${FEE_RECEIVER_ADDRESS.slice(-8)}`
      );
    }

    return {
      success: true,
      txid: txId,
      feeAmount: feeAmount,
      purchasePrice: purchasePrice,
      feeReceiver: FEE_RECEIVER_ADDRESS,
    };
  } catch (err) {
    const errorMsg = err.message || 'Failed to send trading fee';
    if (addConsoleLog) {
      addConsoleLog(`✗ Fee transaction failed: ${errorMsg}`);
    }
    console.error('Error sending trading fee:', err);
    return {
      success: false,
      error: errorMsg,
    };
  }
};

/**
 * Check if a transaction is confirmed (fallback method using mempool)
 * @param {string} txId - Transaction ID
 * @param {string} network - Network type
 * @returns {Promise<boolean>} True if confirmed
 */
export const checkTransactionConfirmed = async (txId, network = 'mainnet') => {
  try {
    const response = await fetch(
      getMempoolTxStatusUrl(txId, network || 'mainnet')
    );
    if (response.ok) {
      const data = await response.json();
      return data.confirmed || false;
    }
    return false;
  } catch (err) {
    console.error('Error checking transaction status:', err);
    return false;
  }
};

/**
 * Verify that a transaction has been accepted into the mempool
 * @param {string} txid - Transaction ID to verify
 * @param {string} network - Network type
 * @param {number} maxRetries - Maximum retry attempts (default: 10)
 * @param {number} retryDelay - Delay between retries in ms (default: 2000)
 * @returns {Promise<boolean>} True if tx is in mempool or confirmed
 */
export const verifyTxInMempool = async (
  txid,
  network = 'mainnet',
  maxRetries = 10,
  retryDelay = 2000
) => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetch(getMempoolTxUrl(txid, network));
      if (response.ok) {
        // Transaction is in mempool or confirmed
        return true;
      }
      if (response.status === 404) {
        // Not yet in mempool, wait and retry
        if (attempt < maxRetries - 1) {
          await new Promise((r) => setTimeout(r, retryDelay));
          continue;
        }
      }
      // Other errors (500, etc.) - retry
      if (attempt < maxRetries - 1) {
        await new Promise((r) => setTimeout(r, retryDelay));
      }
    } catch (err) {
      if (attempt < maxRetries - 1) {
        await new Promise((r) => setTimeout(r, retryDelay));
      }
    }
  }
  return false; // Failed to verify after all retries
};

/**
 * Fetch wallet balance (confirmed)
 * @param {string} address - Wallet address
 * @param {string} network - Network type
 * @returns {Promise<number>} Balance in satoshis
 */
export const fetchWalletBalance = async (address, network = 'mainnet') => {
  try {
    const response = await fetch(
      getMempoolAddressUrl(address, network || 'mainnet')
    );
    if (response.ok) {
      const data = await response.json();
      const confirmedBalance = data.chain_stats?.funded_txo_sum || 0;
      const spentBalance = data.chain_stats?.spent_txo_sum || 0;
      return confirmedBalance - spentBalance;
    }
    return 0;
  } catch (err) {
    console.error('Error fetching wallet balance:', err);
    return 0;
  }
};

/**
 * Delist an ordinal using proxy wallet
 * @param {Object} ordinal - The ordinal to delist
 * @param {Object} wallet - Proxy wallet object
 * @param {string} network - Network type
 * @returns {Promise<Object>} Result with success, error
 */
export const delistOrdinalWithProxyWallet = async (
  ordinal,
  wallet,
  network = 'mainnet'
) => {
  try {
    const tokenId = getTokenId(ordinal);
    if (!tokenId) {
      throw new Error('Could not determine token ID from ordinal');
    }

    let address, publicKey;
    try {
      address = deriveAddressFromPrivateKey(wallet.privateKey, network);
      publicKey = derivePublicKeyFromPrivateKey(wallet.privateKey, network);
    } catch (err) {
      console.error('Error deriving address from private key:', err);
      address = wallet.address;
      publicKey = wallet.publicKey;
    }

    if (!address || !publicKey) {
      throw new Error('Could not get wallet address or public key');
    }

    const isProduction =
      window.location.hostname !== 'localhost' &&
      window.location.hostname !== '127.0.0.1';
    const getPsbtUrl = isProduction
      ? `/api/magiceden-psbt?endpoint=delist&tokenId=${encodeURIComponent(tokenId)}&publicKey=${encodeURIComponent(publicKey)}`
      : `https://api-mainnet.magiceden.us/v2/ord/btc/psbt/delist?tokenId=${encodeURIComponent(tokenId)}&publicKey=${encodeURIComponent(publicKey)}`;

    // Step 1: Fetch delist PSBT
    const fetchResponse = await fetch(getPsbtUrl, {
      method: 'GET',
      headers: {
        accept: 'application/json, text/plain, */*',
        ...(isProduction
          ? {}
          : {
              origin: 'https://magiceden.us',
              referer: 'https://magiceden.us/',
            }),
      },
    });

    if (!fetchResponse.ok) {
      const errorText = await fetchResponse.text();
      let errorMessage = `HTTP error! status: ${fetchResponse.status}`;
      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.error && Array.isArray(errorJson.error)) {
          errorMessage =
            errorJson.error
              .map((err) => err.message || JSON.stringify(err))
              .join(', ') || errorMessage;
        } else if (errorJson.error && typeof errorJson.error === 'string') {
          errorMessage = errorJson.error;
        } else if (errorJson.message) {
          errorMessage = errorJson.message;
        }
      } catch (e) {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const fetchData = await fetchResponse.json();
    // Try multiple possible field names for the unsigned PSBT
    const unsignedDelistPsbt =
      fetchData.unsignedDelistPSBTBase64 ||
      fetchData.unsignedPSBTBase64 ||
      fetchData.delistPSBTBase64 ||
      fetchData.psbtBase64 ||
      '';

    if (!unsignedDelistPsbt) {
      throw new Error('No PSBT returned from delist API');
    }

    // Step 2: Sign PSBT (don't finalize - let Magic Eden finalize it)
    const signResult = await signPsbtWithProxyWallet(
      unsignedDelistPsbt.trim(),
      wallet.privateKey,
      network,
      {
        finalize: false,
        extractTx: false,
        expectedPublicKey: publicKey,
        walletAddress: address,
      }
    );

    if (!signResult || !signResult.base64) {
      throw new Error('Failed to sign delist PSBT with proxy wallet');
    }

    const signedDelistPsbt = signResult.base64;

    // Step 3: Submit delist
    const delistPayload = {
      signedDelistPSBTBase64: signedDelistPsbt,
      tokenId: tokenId,
      publicKey: publicKey,
    };

    const delistApiUrl = isProduction
      ? '/api/magiceden-psbt?endpoint=delist'
      : 'https://api-mainnet.magiceden.us/v2/ord/btc/psbt/delist';

    const delistHeaders = {
      accept: 'application/json, text/plain, */*',
      'content-type': 'application/json',
      ...(isProduction
        ? {
            'cache-control': 'no-cache',
            pragma: 'no-cache',
          }
        : {
            origin: 'https://magiceden.us',
            referer: 'https://magiceden.us/',
          }),
    };

    const delistResponse = await fetch(delistApiUrl, {
      method: 'POST',
      headers: delistHeaders,
      body: JSON.stringify(delistPayload),
    });

    if (!delistResponse.ok) {
      const errorText = await delistResponse.text();
      let errorMessage = `HTTP error! status: ${delistResponse.status}`;
      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.error && Array.isArray(errorJson.error)) {
          errorMessage =
            errorJson.error
              .map((err) => err.message || JSON.stringify(err))
              .join(', ') || errorMessage;
        } else if (errorJson.error && typeof errorJson.error === 'string') {
          errorMessage = errorJson.error;
        } else if (errorJson.message) {
          errorMessage = errorJson.message;
        }
      } catch (e) {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const delistData = await delistResponse.json();

    return {
      success: true,
      data: delistData,
    };
  } catch (error) {
    console.error('Error delisting ordinal:', error);
    return {
      success: false,
      error: error.message || 'Unknown error',
    };
  }
};
